import React, { useMemo, useState, useCallback } from "react";
import { Ic, Modal, StatusBadge, inputStyle, btnPrimary, btnSecondary, btnGhost, showStyledAlert, showStyledConfirm } from "../components/UI";
import { updateUser, deleteUser, addUser, syncUserVolunteer } from "../utils/firestore";
import { adminSetUserPassword } from "../utils/adminAuth";

/* ─── Inline Icons ───────────────────────────────────────────── */
function UserPlusIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>);
}
function XIcon({ size = 18 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
}
function ChevronRightIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);
}
function ShieldIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>);
}
function MailIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>);
}
function UsersIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
}
function LinkIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>);
}
function KeyIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>);
}
function PowerIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></svg>);
}
function UserIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
}

/* ─── Shared Style Constants ─────────────────────────────────── */
const ACCENT = "#C41E3A";
const ACCENT_LIGHT = "#FFF1F3";
const BORDER = "#E5E7EB";
const BORDER_LIGHT = "#F3F4F6";
const TEXT_PRIMARY = "#1F2937";
const TEXT_SECONDARY = "#6B7280";
const TEXT_MUTED = "#9CA3AF";
const BG_SUBTLE = "#F9FAFB";

const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: `1px solid ${BORDER}`, background: "white", cursor: "pointer", color: TEXT_SECONDARY, transition: "all 0.15s" };
const iconBtnAccent = { ...iconBtn, background: ACCENT, border: `1px solid ${ACCENT}`, color: "white" };

const PERMISSION_OPTIONS = [
  { key: "addVolunteers", label: "הוספת מתנדבים" },
  { key: "addActivities", label: "הוספת פעילות" },
  { key: "addRecurring", label: "הוספת פעילות קבועה" },
  { key: "addContributionTypes", label: "הוספת סוג תרומה" },
  { key: "editDomains", label: "עריכת תחומים" },
  { key: "addBeneficiaries", label: "הוספת נעזרים" },
  { key: "manageUsers", label: "משתמשים" },
  { key: "viewReports", label: "צפיה בדוחות" },
];

/* ─── Role helpers ───────────────────────────────────────────── */
const ROLE_MAP = { coordinator: "רכז", group_coordinator: "תפקיד מורשת", volunteer: "מתנדב" };
const ROLE_COLORS = {
  coordinator: { bg: "#EFF6FF", color: "#2563EB", border: "#DBEAFE" },
  group_coordinator: { bg: "#F3F4F6", color: "#4B5563", border: "#E5E7EB" },
  volunteer: { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
};
function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.volunteer;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: "Heebo", background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {ROLE_MAP[role] || role}
    </span>
  );
}

function AvatarColor(role) {
  if (role === "coordinator") return "linear-gradient(135deg, #2563EB, #60A5FA)";
  if (role === "group_coordinator") return "linear-gradient(135deg, #6B7280, #9CA3AF)";
  return "linear-gradient(135deg, #F59E0B, #FBBF24)";
}

/* ─── User Drawer ────────────────────────────────────────────── */
function UserDrawer({ user, onClose, coordinators, domains = [], volunteerById, userById, savingUserId, canManageSuperAdmin, onSetRole, onSetParent, onSetManagedDomains, onSetPermissions, onToggleActive, onToggleSuperAdmin, onSyncVolunteer, onRemove, onOpenPassword, isMobile }) {
  const [closing, setClosing] = useState(false);
  const handleClose = useCallback(() => { setClosing(true); setTimeout(() => onClose(), 260); }, [onClose]);
  const drawerWidth = isMobile ? "100vw" : 440;
  const isSaving = savingUserId === user.id;
  const activeDomains = useMemo(() => domains.filter((d) => d.active !== false), [domains]);
  const [selectedManagedMainDomainId, setSelectedManagedMainDomainId] = useState("");
  const [selectedManagedSubdomainName, setSelectedManagedSubdomainName] = useState("");
  const managedDomainIds = Array.isArray(user.managedDomainIds) ? user.managedDomainIds : [];
  const domainById = useMemo(() => {
    const m = {};
    activeDomains.forEach((d) => { m[d.id] = d; });
    return m;
  }, [activeDomains]);
  const isMainCategory = (d) => d?.isMainCategory !== false;
  const mainDomainOptions = useMemo(
    () => activeDomains.filter((d) => isMainCategory(d)).sort((a, b) => (a.name || "").localeCompare((b.name || ""), "he")),
    [activeDomains]
  );
  const childDomainsByMainId = useMemo(() => {
    const m = {};
    activeDomains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      if (!m[parentId]) m[parentId] = [];
      m[parentId].push(d);
    });
    Object.keys(m).forEach((k) => {
      m[k].sort((a, b) => {
        const nameCmp = (a.name || "").localeCompare((b.name || ""), "he");
        if (nameCmp !== 0) return nameCmp;
        return (a.city || "").localeCompare((b.city || ""), "he");
      });
    });
    return m;
  }, [activeDomains]);
  const childDomainOptions = useMemo(
    () => childDomainsByMainId[selectedManagedMainDomainId] || [],
    [childDomainsByMainId, selectedManagedMainDomainId]
  );
  const subdomainNameOptions = useMemo(() => {
    const seen = new Set();
    const rows = [];
    childDomainOptions.forEach((d) => {
      const name = (d.name || "").trim();
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return;
      seen.add(key);
      rows.push(name);
    });
    return rows.sort((a, b) => a.localeCompare(b, "he"));
  }, [childDomainOptions]);
  const matchingSubdomainDomainIds = useMemo(() => {
    if (!selectedManagedSubdomainName) return [];
    return childDomainOptions
      .filter((d) => (d.name || "").trim() === selectedManagedSubdomainName)
      .map((d) => d.id);
  }, [childDomainOptions, selectedManagedSubdomainName]);
  const permissions = user.permissions && typeof user.permissions === "object" ? user.permissions : {};
  const domainLabel = (d) => {
    if (!d) return "—";
    const parent = (d.parentDomainId || "").trim() ? domainById[(d.parentDomainId || "").trim()] : null;
    const city = (d.city || "").trim();
    const base = parent ? `${parent.name} / ${d.name}` : d.name;
    return `${base}${city ? ` (${city})` : ""}`;
  };

  const InfoRow = ({ icon, label, value, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
      <div style={{ color: TEXT_MUTED, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED, marginBottom: 2 }}>{label}</div>
        {children || <div style={{ fontSize: 14, color: TEXT_PRIMARY, wordBreak: "break-word" }}>{value || "—"}</div>}
      </div>
    </div>
  );

  return (
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998, opacity: closing ? 0 : 1, transition: "opacity 0.25s ease", backdropFilter: "blur(2px)" }} />
      <div dir="rtl" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: drawerWidth, maxWidth: "100vw", background: "white", zIndex: 9999, boxShadow: "-8px 0 30px rgba(0,0,0,0.12)", transform: closing ? "translateX(100%)" : "translateX(0)", transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)", display: "flex", flexDirection: "column", animation: "drawerSlideIn 0.28s cubic-bezier(0.4, 0, 0.2, 1)" }}>
        <style>{`@keyframes drawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${BORDER}`, background: BG_SUBTLE }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={handleClose} style={{ ...iconBtn, width: 32, height: 32 }}><XIcon size={16} /></button>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={{ ...iconBtn, color: "#EF4444" }} onClick={() => onRemove(user)} title="מחיקה" disabled={isSaving}><Ic.Trash /></button>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: AvatarColor(user.role), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, fontFamily: "Assistant", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
              {(user.name || user.email || "?")[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "Assistant", color: TEXT_PRIMARY, marginBottom: 4 }}>{user.name || "ללא שם"}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <StatusBadge status={user.active ? "active" : "inactive"} />
                <RoleBadge role={user.role || "volunteer"} />
                {user.isSuperAdmin && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>
                    <ShieldIcon size={10} /> סופר אדמין
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Details + Controls */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 24px" }}>
          <InfoRow icon={<MailIcon />} label="אימייל">
            <div style={{ fontSize: 14, color: TEXT_PRIMARY, direction: "ltr", textAlign: "right" }}>{user.email || "—"}</div>
          </InfoRow>

          <InfoRow icon={<UserIcon />} label="תפקיד">
            <select
              style={{ ...inputStyle, width: "100%", padding: "8px 12px", cursor: "pointer", fontSize: 14 }}
              value={user.role || "volunteer"}
              disabled={isSaving}
              onChange={(e) => onSetRole(user, e.target.value)}
            >
              <option value="coordinator">רכז</option>
              <option value="volunteer">מתנדב</option>
              {user.role === "group_coordinator" && <option value="group_coordinator">תפקיד מורשת (יש להמיר)</option>}
            </select>
          </InfoRow>

          {user.role === "group_coordinator" && <InfoRow icon={<UsersIcon />} label="מורשת">{`קיים משתמש מורשת מסוג ישן. מומלץ להמיר למתנדב/רכז.`}</InfoRow>}

          <InfoRow icon={<LinkIcon />} label="שיוך מתנדב">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: TEXT_PRIMARY }}>{user.volunteerId ? (volunteerById[user.volunteerId]?.name || "משויך") : "ללא שיוך"}</span>
              {!user.volunteerId && (user.role === "group_coordinator" || user.role === "volunteer") && (
                <button
                  style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
                  disabled={isSaving}
                  onClick={() => onSyncVolunteer(user)}
                >
                  <LinkIcon size={12} /> שייך
                </button>
              )}
            </div>
          </InfoRow>

          {user.role === "coordinator" && (
            <InfoRow icon={<UsersIcon />} label="תחומים באחריות">
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <select
                    style={{ ...inputStyle, width: "100%", padding: "8px 12px", cursor: "pointer", fontSize: 14 }}
                    value={selectedManagedMainDomainId}
                    disabled={isSaving}
                    onChange={(e) => {
                      setSelectedManagedMainDomainId(e.target.value);
                      setSelectedManagedSubdomainName("");
                    }}
                  >
                    <option value="">בחר תחום ראשי</option>
                    {mainDomainOptions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  <select
                    style={{ ...inputStyle, width: "100%", padding: "8px 12px", cursor: selectedManagedMainDomainId ? "pointer" : "not-allowed", fontSize: 14, opacity: selectedManagedMainDomainId ? 1 : 0.8 }}
                    value={selectedManagedSubdomainName}
                    disabled={isSaving || !selectedManagedMainDomainId}
                    onChange={(e) => {
                      setSelectedManagedSubdomainName(e.target.value);
                    }}
                  >
                    <option value="">{selectedManagedMainDomainId ? "ללא תת־תחום (ראשי בלבד)" : "בחר קודם תחום ראשי"}</option>
                    {subdomainNameOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                  <div style={{ ...inputStyle, width: "100%", padding: "10px 12px", fontSize: 13, color: TEXT_MUTED, background: "#FAFAFC" }}>
                    {selectedManagedSubdomainName
                      ? "בחירה בתת־תחום תוסיף את כל הרשומות המשויכות אליו, בלי שלב עיר נפרד."
                      : "אפשר להוסיף תחום ראשי שלם או תת־תחום."}
                  </div>
                  <button
                    style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
                    disabled={isSaving || !selectedManagedMainDomainId}
                    onClick={() => {
                      let idsToAdd = [];
                      if (!selectedManagedSubdomainName) {
                        idsToAdd = [selectedManagedMainDomainId];
                      } else {
                        idsToAdd = matchingSubdomainDomainIds;
                      }
                      if (!idsToAdd.length) return;
                      const next = Array.from(new Set([...(managedDomainIds || []), ...idsToAdd]));
                      onSetManagedDomains(user, next);
                    }}
                  >
                    <Ic.Plus /> הוסף
                  </button>
                </div>
                <div style={{ fontSize: 11, color: TEXT_MUTED }}>
                  בחר תחום ראשי, ואז תת־תחום אם צריך. שלב העיר הוסר.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {managedDomainIds.length === 0 && <span style={{ fontSize: 12, color: TEXT_MUTED }}>אין שיוך תחומים</span>}
                  {managedDomainIds.map((id) => {
                    const d = domainById[id] || domains.find((x) => x.id === id);
                    return (
                      <button
                        key={id}
                        type="button"
                        disabled={isSaving}
                        onClick={() => onSetManagedDomains(user, managedDomainIds.filter((x) => x !== id))}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, border: `1px solid ${BORDER}`, background: BG_SUBTLE, color: TEXT_PRIMARY, fontSize: 12, cursor: "pointer" }}
                        title="לחץ להסרה"
                      >
                        {domainLabel(d)} ✕
                      </button>
                    );
                  })}
                </div>
              </div>
            </InfoRow>
          )}

          {user.role === "coordinator" && (
            <InfoRow icon={<ShieldIcon />} label="הרשאות ביצוע">
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 12, color: TEXT_MUTED }}>
                  רכזים רואים את כל המערכת. ההרשאות כאן שולטות רק על פעולות ביצוע ושינוי.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                  {PERMISSION_OPTIONS.map((permission) => {
                    const checked = permissions[permission.key] !== false;
                    return (
                      <label
                        key={permission.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          border: `1px solid ${BORDER}`,
                          borderRadius: 10,
                          padding: "10px 12px",
                          background: checked ? "#F9FAFB" : "white",
                          cursor: isSaving ? "not-allowed" : "pointer",
                        }}
                      >
                        <span style={{ fontSize: 13, color: TEXT_PRIMARY }}>{permission.label}</span>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isSaving}
                          onChange={(e) => {
                            const next = { ...permissions, [permission.key]: e.target.checked };
                            onSetPermissions(user, next);
                          }}
                          style={{ accentColor: ACCENT }}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </InfoRow>
          )}

          <InfoRow icon={<ShieldIcon />} label="סופר אדמין">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: user.isSuperAdmin ? "#92400E" : TEXT_MUTED, fontWeight: user.isSuperAdmin ? 700 : 400 }}>
                {user.isSuperAdmin ? "כן" : "לא"}
              </span>
              {canManageSuperAdmin && (
                <button
                  style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
                  disabled={isSaving}
                  onClick={() => onToggleSuperAdmin(user)}
                >
                  {user.isSuperAdmin ? "הסר" : "הפעל"}
                </button>
              )}
            </div>
          </InfoRow>

          {/* Action buttons */}
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "12px 0", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Heebo",
                cursor: "pointer", border: `1px solid ${BORDER}`, background: "white", color: TEXT_PRIMARY,
                transition: "all 0.15s",
              }}
              disabled={isSaving}
              onClick={() => onToggleActive(user)}
            >
              <PowerIcon size={15} />
              {user.active ? "השבת משתמש" : "הפעל משתמש"}
            </button>

            {canManageSuperAdmin && (
              <button
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "12px 0", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Heebo",
                  cursor: "pointer", border: `1px solid ${BORDER}`, background: "white", color: TEXT_PRIMARY,
                  transition: "all 0.15s",
                }}
                disabled={isSaving}
                onClick={() => onOpenPassword(user)}
              >
                <KeyIcon size={15} />
                הגדר / החלף סיסמה
              </button>
            )}

            <button
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "12px 0", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Heebo",
                cursor: "pointer", border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626",
                transition: "all 0.15s",
              }}
              disabled={isSaving}
              onClick={() => onRemove(user)}
            >
              <Ic.Trash />
              מחיקת משתמש
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function UsersPage({ users, volunteers, domains = [], onRefresh, showToast, currentUserId, isMobile = false, canManageSuperAdmin = false }) {
  const [savingUserId, setSavingUserId] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [drawerUser, setDrawerUser] = useState(null);
  const [search, setSearch] = useState("");
  const PRIMARY_ADMIN_EMAIL = "studiozula148@gmail.com";
  const coordinators = users.filter((u) => u.role === "coordinator");
  const userById = useMemo(() => { const m = {}; users.forEach((u) => { m[u.id] = u; }); return m; }, [users]);
  const volunteerById = useMemo(() => { const m = {}; volunteers.forEach((v) => { m[v.id] = v; }); return m; }, [volunteers]);

  const filteredUsers = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => [u.name || "", u.email || "", ROLE_MAP[u.role] || ""].join(" ").toLowerCase().includes(q));
  }, [users, search]);

  const setRole = async (user, role) => {
    if (user.id === currentUserId && role !== "coordinator") {
      await showStyledAlert("לא ניתן להוריד את עצמך מתפקיד ניהולי."); return;
    }
    setSavingUserId(user.id);
    try {
      const patch = { role };
      if (role !== "group_coordinator") patch.parentCoordinatorId = "";
      await updateUser(user.id, patch);
      if (role === "volunteer" || role === "group_coordinator") await syncUserVolunteer(user.id);
      showToast("התפקיד עודכן");
      // Update drawer if open
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, ...patch });
      onRefresh();
    } catch (e) { await showStyledAlert("שגיאה בעדכון תפקיד: " + e.message); }
    setSavingUserId("");
  };

  const setParentCoordinator = async (user, parentCoordinatorId) => {
    setSavingUserId(user.id);
    try {
      await updateUser(user.id, { parentCoordinatorId: parentCoordinatorId || "" });
      showToast("שיוך מנהל מורשת עודכן");
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, parentCoordinatorId });
      onRefresh();
    } catch (e) { await showStyledAlert("שגיאה בעדכון שיוך: " + e.message); }
    setSavingUserId("");
  };

  const setManagedDomains = async (user, managedDomainIds) => {
    setSavingUserId(user.id);
    try {
      await updateUser(user.id, { managedDomainIds: Array.isArray(managedDomainIds) ? managedDomainIds : [] });
      showToast("שיוך תחומים עודכן");
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, managedDomainIds: Array.isArray(managedDomainIds) ? managedDomainIds : [] });
      onRefresh();
    } catch (e) { await showStyledAlert("שגיאה בעדכון שיוך תחומים: " + e.message); }
    setSavingUserId("");
  };

  const setPermissions = async (user, permissions) => {
    setSavingUserId(user.id);
    try {
      await updateUser(user.id, { permissions });
      showToast("הרשאות עודכנו");
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, permissions });
      onRefresh();
    } catch (e) {
      await showStyledAlert("שגיאה בעדכון הרשאות: " + e.message);
    }
    setSavingUserId("");
  };

  const toggleActive = async (user) => {
    if (user.id === currentUserId && user.active) {
      await showStyledAlert("לא ניתן להשבית את המשתמש שמחובר כרגע."); return;
    }
    setSavingUserId(user.id);
    try {
      await updateUser(user.id, { active: !user.active });
      showToast(user.active ? "המשתמש הושבת" : "המשתמש הופעל");
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, active: !user.active });
      onRefresh();
    } catch (e) { await showStyledAlert("שגיאה בעדכון סטטוס: " + e.message); }
    setSavingUserId("");
  };

  const addNew = async (form) => {
    try { await addUser(form); showToast("משתמש נוסף"); setShowAddModal(false); onRefresh(); }
    catch (e) { await showStyledAlert("שגיאה בהוספת משתמש: " + e.message); }
  };

  const syncVolunteerLink = async (user) => {
    setSavingUserId(user.id);
    try { await syncUserVolunteer(user.id); showToast("שיוך מתנדב עודכן"); onRefresh(); }
    catch (e) { await showStyledAlert("שגיאה בסנכרון מתנדב: " + e.message); }
    setSavingUserId("");
  };

  const removeUser = async (user) => {
    if (user.id === currentUserId) { await showStyledAlert("לא ניתן למחוק את המשתמש שמחובר כרגע."); return; }
    if (!await showStyledConfirm(`למחוק את המשתמש ${user.name || user.email || user.id}?`)) return;
    setSavingUserId(user.id);
    try { await deleteUser(user.id); showToast("המשתמש נמחק"); setDrawerUser(null); onRefresh(); }
    catch (e) { await showStyledAlert("שגיאה במחיקת משתמש: " + e.message); }
    setSavingUserId("");
  };

  const toggleSuperAdmin = async (user) => {
    if (!canManageSuperAdmin) return;
    if ((user.email || "").trim().toLowerCase() === PRIMARY_ADMIN_EMAIL && user.isSuperAdmin) {
      await showStyledAlert("לא ניתן להסיר הרשאת סופר אדמין ממנהל העל."); return;
    }
    setSavingUserId(user.id);
    try {
      await updateUser(user.id, { isSuperAdmin: !user.isSuperAdmin });
      showToast(user.isSuperAdmin ? "הרשאת סופר אדמין הוסרה" : "הרשאת סופר אדמין נוספה");
      if (drawerUser?.id === user.id) setDrawerUser({ ...user, isSuperAdmin: !user.isSuperAdmin });
      onRefresh();
    } catch (e) { await showStyledAlert("שגיאה בעדכון סופר אדמין: " + e.message); }
    setSavingUserId("");
  };

  const openPasswordModal = async (user) => {
    if (!canManageSuperAdmin) return;
    if (!(user.email || "").trim()) { await showStyledAlert("לא ניתן להגדיר סיסמה למשתמש ללא אימייל."); return; }
    setPasswordModalUser(user);
  };

  const handleSetUserPassword = async ({ userId, newPassword, revokeSessions }) => {
    try {
      const result = await adminSetUserPassword({ userId, newPassword, revokeSessions });
      setPasswordModalUser(null);
      showToast(result?.createdAuthUser ? "נוצר חשבון התחברות והוגדרה סיסמה" : "הסיסמה עודכנה בהצלחה");
      onRefresh();
    } catch (e) {
      const code = e?.code || "";
      const map = {
        "functions/unauthenticated": "נדרשת התחברות מחדש כדי לבצע פעולה זו.",
        "functions/permission-denied": "רק סופר אדמין יכול להגדיר סיסמה למשתמשים.",
        "functions/not-found": "המשתמש לא נמצא במערכת.",
        "functions/invalid-argument": "נתוני הסיסמה לא תקינים.",
        "functions/failed-precondition": "לא ניתן להגדיר סיסמה למשתמש ללא אימייל.",
      };
      await showStyledAlert(map[code] || e?.message || "שגיאה בהגדרת סיסמה.");
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: BG_SUBTLE, borderRadius: 10, padding: "8px 14px", width: isMobile ? "100%" : 240, border: `1px solid ${BORDER}` }}>
            <Ic.Search />
            <input placeholder="חיפוש משתמש..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontSize: 14, width: "100%", direction: "rtl", fontFamily: "Heebo" }} />
          </div>
        </div>
        <button style={iconBtnAccent} onClick={() => setShowAddModal(true)} title="משתמש חדש"><UserPlusIcon size={17} /></button>
      </div>

      {/* Results */}
      <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "12px 16px" : "14px 20px", borderBottom: `1px solid ${BORDER}`, background: BG_SUBTLE }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "Heebo" }}>{filteredUsers.length} משתמשים</span>
          {search && <button onClick={() => setSearch("")} style={{ ...btnGhost, fontSize: 12, color: ACCENT }}>נקה חיפוש</button>}
        </div>

        {/* Desktop table */}
        {!isMobile ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["שם", "אימייל", "תפקיד", "סטטוס", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: TEXT_MUTED, textAlign: "right", background: "white", borderBottom: `1px solid ${BORDER}`, textTransform: "uppercase", letterSpacing: "0.3px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} style={{ cursor: "pointer", transition: "background 0.1s" }} onClick={() => setDrawerUser(user)} onMouseEnter={e => e.currentTarget.style.background = BG_SUBTLE} onMouseLeave={e => e.currentTarget.style.background = "white"}>
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: AvatarColor(user.role), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: "Assistant" }}>
                        {(user.name || user.email || "?")[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: TEXT_PRIMARY }}>{user.name || "ללא שם"}</span>
                          {user.isSuperAdmin && <ShieldIcon size={12} />}
                        </div>
                        {user.id === currentUserId && <span style={{ fontSize: 11, color: ACCENT, fontWeight: 600 }}>את/ה</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 13, color: TEXT_SECONDARY, direction: "ltr", textAlign: "right" }}>{user.email || "—"}</td>
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><RoleBadge role={user.role || "volunteer"} /></td>
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><StatusBadge status={user.active ? "active" : "inactive"} /></td>
                  <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                    <div style={{ display: "flex", alignItems: "center", color: TEXT_MUTED }}><ChevronRightIcon size={16} /></div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>אין משתמשים להצגה
                </td></tr>
              )}
            </tbody>
          </table>
        ) : (
          /* Mobile cards */
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredUsers.map((user) => (
              <div key={user.id} onClick={() => setDrawerUser(user)} style={{ border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, cursor: "pointer", background: "white", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: AvatarColor(user.role), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, fontFamily: "Assistant" }}>
                  {(user.name || user.email || "?")[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: TEXT_PRIMARY, fontFamily: "Assistant" }}>{user.name || "ללא שם"}</span>
                      {user.isSuperAdmin && <ShieldIcon size={12} />}
                    </div>
                    <StatusBadge status={user.active ? "active" : "inactive"} />
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_SECONDARY, direction: "ltr", textAlign: "right", marginBottom: 4 }}>{user.email || "—"}</div>
                  <RoleBadge role={user.role || "volunteer"} />
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}><div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>אין משתמשים להצגה</div>
            )}
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawerUser && (
        <UserDrawer
          user={drawerUser}
          onClose={() => setDrawerUser(null)}
          coordinators={coordinators}
          domains={domains}
          volunteerById={volunteerById}
          userById={userById}
          savingUserId={savingUserId}
          canManageSuperAdmin={canManageSuperAdmin}
          onSetRole={setRole}
          onSetParent={setParentCoordinator}
          onSetManagedDomains={setManagedDomains}
          onSetPermissions={setPermissions}
          onToggleActive={toggleActive}
          onToggleSuperAdmin={toggleSuperAdmin}
          onSyncVolunteer={syncVolunteerLink}
          onRemove={removeUser}
          onOpenPassword={openPasswordModal}
          isMobile={isMobile}
        />
      )}

      {showAddModal && <AddUserModal coordinators={coordinators} onClose={() => setShowAddModal(false)} onSave={addNew} />}
      {passwordModalUser && <SetUserPasswordModal user={passwordModalUser} onClose={() => setPasswordModalUser(null)} onSave={handleSetUserPassword} />}
    </div>
  );
}

/* ─── Add User Modal ─────────────────────────────────────────── */
function AddUserModal({ onClose, onSave, coordinators = [] }) {
  const [f, setF] = useState({ name: "", email: "", role: "volunteer", parentCoordinatorId: "", createVolunteerProfile: true, active: true });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 6 };

  const submit = async () => {
    setSaving(true);
    await onSave({ ...f, parentCoordinatorId: "" });
    setSaving(false);
  };

  return (
    <Modal title="משתמש חדש" onClose={onClose} footer={<><button style={{ ...btnPrimary, ...(!f.email && { opacity: 0.5, pointerEvents: "none" }) }} onClick={submit} disabled={saving}><Ic.Check />{saving ? "שומר..." : "יצירה"}</button><button style={btnSecondary} onClick={onClose}>ביטול</button></>}>
      <div style={{ marginBottom: 16 }}><label style={labelStyle}>שם</label><input style={inputStyle} value={f.name} onChange={(e) => u("name", e.target.value)} /></div>
      <div style={{ marginBottom: 16 }}><label style={labelStyle}>אימייל</label><input style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={f.email} onChange={(e) => u("email", e.target.value)} /></div>
      <div style={{ marginBottom: 16 }}><label style={labelStyle}>תפקיד</label><select style={{ ...inputStyle, cursor: "pointer" }} value={f.role} onChange={(e) => u("role", e.target.value)}><option value="volunteer">מתנדב</option><option value="coordinator">רכז</option></select></div>
      {f.role === "volunteer" && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={f.createVolunteerProfile} onChange={(e) => u("createVolunteerProfile", e.target.checked)} />
            ליצור/לשייך גם פרופיל מתנדב
          </label>
        </div>
      )}
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}><input type="checkbox" checked={f.active} onChange={(e) => u("active", e.target.checked)} /> פעיל</label>
    </Modal>
  );
}

/* ─── Set Password Modal ─────────────────────────────────────── */
function SetUserPasswordModal({ user, onClose, onSave }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [revokeSessions, setRevokeSessions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 6 };

  const submit = async () => {
    if ((password || "").length < 8) { await showStyledAlert("יש להזין סיסמה באורך 8 תווים לפחות."); return; }
    if (password !== confirmPassword) { await showStyledAlert("הסיסמאות אינן תואמות."); return; }
    setSaving(true);
    try { await onSave({ userId: user.id, newPassword: password, revokeSessions }); } finally { setSaving(false); }
  };

  return (
    <Modal title="הגדרת סיסמה למשתמש" onClose={saving ? () => {} : onClose} footer={<><button style={{ ...btnPrimary, ...(saving ? { opacity: 0.7 } : {}) }} onClick={submit} disabled={saving}><Ic.Check />{saving ? "שומר..." : "שמירת סיסמה"}</button><button style={btnSecondary} onClick={onClose} disabled={saving}>ביטול</button></>}>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ background: BG_SUBTLE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 12, fontSize: 13, color: TEXT_PRIMARY }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{user.name || "ללא שם"}</div>
          <div style={{ direction: "ltr", textAlign: "right", color: TEXT_SECONDARY }}>{user.email || "ללא אימייל"}</div>
        </div>
        <div>
          <label style={labelStyle}>סיסמה חדשה</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type={showPassword ? "text" : "password"} style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            <button type="button" style={btnSecondary} onClick={() => setShowPassword((v) => !v)}><Ic.Eye />{showPassword ? "הסתר" : "הצג"}</button>
          </div>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4 }}>מומלץ: לפחות 8 תווים, עם אותיות ומספרים.</div>
        </div>
        <div>
          <label style={labelStyle}>אימות סיסמה</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type={showConfirmPassword ? "text" : "password"} style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
            <button type="button" style={btnSecondary} onClick={() => setShowConfirmPassword((v) => !v)}><Ic.Eye />{showConfirmPassword ? "הסתר" : "הצג"}</button>
          </div>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: TEXT_PRIMARY }}>
          <input type="checkbox" checked={revokeSessions} onChange={(e) => setRevokeSessions(e.target.checked)} />
          לנתק את המשתמש מכל המכשירים (מומלץ)
        </label>
      </div>
    </Modal>
  );
}
